import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLines } from "../motion";
import { CtaButton } from "../Buttons";

const Sep = () => <span aria-hidden="true" className="ml-[0.18em] inline-block h-[0.72em] w-[0.08em] translate-y-[0.02em] bg-[color:var(--ngp-teal)] align-baseline" />;

const RegBadge = ({ mx, my }) => (
  <motion.div style={{ rotateX: my, rotateY: mx }} className="pointer-events-none absolute right-6 top-28 hidden h-36 w-36 [perspective:600px] md:block lg:right-14 lg:top-32 lg:h-44 lg:w-44">
    <svg viewBox="0 0 200 200" className="spin-slow h-full w-full text-white/80" aria-hidden="true">
      <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <text fontSize="13" letterSpacing="5" fill="currentColor" fontFamily="General Sans, sans-serif">
        <textPath href="#circ" textLength="486" lengthAdjust="spacingAndGlyphs">GIGA PRINT · ROMA · STAMPA · ALLESTIMENTI ·</textPath>
      </text>
    </svg>
    <svg viewBox="0 0 100 100" className="absolute inset-0 m-auto h-14 w-14" aria-hidden="true">
      <circle cx="50" cy="50" r="22" fill="none" stroke="#00A8B4" strokeWidth="3" />
      <line x1="50" y1="12" x2="50" y2="88" stroke="#fff" strokeWidth="1.5" />
      <line x1="12" y1="50" x2="88" y2="50" stroke="#fff" strokeWidth="1.5" />
      <circle cx="50" cy="50" r="6" fill="#00A8B4" />
    </svg>
  </motion.div>
);

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, reduce ? 1.08 : 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-12%"]);
  const mxRaw = useMotionValue(0), myRaw = useMotionValue(0);
  const mx = useSpring(mxRaw, { stiffness: 60, damping: 18 }), my = useSpring(myRaw, { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mxRaw.set(((e.clientX - r.left) / r.width - 0.5) * 30);
    myRaw.set(-((e.clientY - r.top) / r.height - 0.5) * 30);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="grain relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-[color:var(--ngp-ink)] text-white">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 -z-10">
        <motion.img
          src="/img/hero-printer.webp"
          alt="Plotter di stampa grande formato in lavorazione nel laboratorio"
          className="h-full w-full object-cover"
          initial={reduce ? false : { clipPath: "inset(12% 8% 12% 40%)", opacity: 0.4 }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", opacity: 1 }}
          transition={{ duration: 1.6, delay: 0.1, ease: [0.7, 0, 0.2, 1] }}
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#1d1d1b] via-[#1d1d1b]/70 to-[#1d1d1b]/40" />
      <div className="absolute inset-y-0 left-0 -z-10 w-full bg-gradient-to-r from-[#1d1d1b]/85 via-[#1d1d1b]/30 to-transparent" />
      <RegBadge mx={mx} my={my} />

      <motion.div style={{ y: textY }} className="mx-auto flex w-full max-w-[1440px] flex-1 flex-col justify-end px-5 pb-10 pt-32 sm:px-8 lg:px-12 lg:pb-14">
        <h1 data-testid="hero-title" className="font-display text-[10.4vw] font-semibold uppercase leading-[0.88] tracking-[-0.035em] sm:text-[10vw] lg:text-[8.6vw] 2xl:text-[132px]">
          <span className="sr-only">Stampa | Allestimenti | Creatività | Digital Marketing</span>
          <span aria-hidden="true">
            <MaskLines delay={0.35} lines={[
              <>Stampa<Sep /></>,
              <span className="text-white/90">Allestimenti<Sep /></span>,
              <>Creatività<Sep /></>,
              <span className="text-[color:var(--ngp-teal)]">Digital Marketing</span>,
            ]} />
          </span>
        </h1>
        <motion.div initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.9 }}
          className="mt-10 grid gap-8 border-t border-white/15 pt-8 md:grid-cols-12 md:items-end">
          <p data-testid="hero-subtitle" className="max-w-md text-base leading-relaxed text-white/85 md:col-span-5 md:text-lg">
            Comunicazione visiva su misura, dalla stampa all'allestimento. Rispetto dei tempi di consegna garantito.
          </p>
          <div className="flex flex-wrap gap-3 md:col-span-7 md:justify-end">
            <CtaButton onClick={onQuote} testId="hero-quote-btn">Richiedi preventivo</CtaButton>
            <CtaButton href="#servizi" variant="ghost" testId="hero-services-btn">Scopri i servizi</CtaButton>
          </div>
        </motion.div>
      </motion.div>
      <a href="#servizi" aria-label="Scorri ai servizi" className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-white/70 lg:block" data-testid="hero-scroll-indicator">
        <ArrowDown size={18} className="animate-bounce" />
      </a>
    </section>
  );
};
